import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import {
  CellInput,
  ConditionValue,
  FileRef,
  GridCell,
  InputSheet,
  InputValues,
  ResolvedCondition,
  SweepCellChange,
  SweepInput,
  formatConditionValue,
  isInputFilled,
  setSweepCell,
  shelfOrder,
  sweepGrid,
  sweepView,
  traceKey,
} from '@/reports/inputs';
import { RunTrace } from '@/reports/runs';
import { Input } from '../ui/input';
import { NumberControl, Segmented, cellInput, cellSelect } from './fields';
import { FileControl, WaveformControl, captureColumns } from './fileFields';
import { useFill } from './fillContext';

type At = Record<string, ConditionValue>;

/** Every combination of some conditions' values, the first changing slowest. */
function combos(conditions: ResolvedCondition[]): At[] {
  let all: At[] = [{}];
  for (const c of conditions) all = all.flatMap((at) => c.values.map((v) => ({ ...at, [c.id]: v })));
  return conditions.some((c) => c.values.length === 0) ? [] : all;
}

const describe = (conditions: ResolvedCondition[], at: At) => conditions.map((c) => formatConditionValue(c, at[c.id])).join(' · ');

interface CellEditorProps {
  field: CellInput;
  cell: GridCell;
  sweepId: string;
  value: unknown;
  traces: Record<string, RunTrace>;
  onChange: (value: unknown) => void;
  nav: { row: number; col: number };
  needed: boolean;
  label: string;
}

/** One recorded input of one cell, edited in place like a spreadsheet cell. */
function CellEditor({ field, cell, sweepId, value, traces, onChange, nav, needed, label }: CellEditorProps) {
  const { editable } = useFill();
  const data = { 'data-cell': cell.key, 'data-field': field.id, 'data-row': String(nav.row), 'data-col': String(nav.col) };
  const blank = value === null || value === undefined || value === '';
  const flat = cellInput(blank);
  const ring = needed ? 'ring-1 ring-inset ring-amber-400' : '';
  if (!editable) {
    const shown = blank ? '—' : typeof value === 'boolean' ? (value ? I18n.get('yes') : I18n.get('no')) : Array.isArray(value) || typeof value === 'object' ? '…' : String(value);
    return <span className={cn('block px-1 tabular-nums', !blank && 'font-medium')}>{shown}</span>;
  }
  switch (field.kind) {
    case 'number':
      return <NumberControl {...data} aria-label={label} value={typeof value === 'number' ? value : null} placeholder={I18n.get('rv2TypeAValue')} onChange={onChange} inputClassName={cn(flat, ring)} />;
    case 'text':
      return <Input {...data} aria-label={label} value={typeof value === 'string' ? value : ''} placeholder={I18n.get('rv2TypeAValue')} onChange={(e) => onChange(e.target.value)} className={cn(flat, ring)} />;
    case 'date':
      return <Input {...data} type="date" aria-label={label} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} className={cn(flat, ring)} />;
    case 'choice':
      return (
        <select {...data} aria-label={label} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} className={cn(cellSelect(blank), ring)}>
          <option value="">{I18n.get('selectDot')}</option>
          {field.choices.map((c, i) => <option key={i} value={c}>{c}</option>)}
        </select>
      );
    case 'yesno':
      return <div><Segmented size="sm" value={typeof value === 'boolean' ? value : null} onChange={onChange} options={[{ value: true, label: I18n.get('yes') }, { value: false, label: I18n.get('no') }]} /></div>;
    case 'image':
    case 'file':
      return <div className="py-1"><FileControl compact value={Array.isArray(value) ? (value as FileRef[]) : []} multiple={field.multiple} accept={field.kind === 'image' ? ['.jpg', '.jpeg', '.png', '.gif', '.webp'] : field.accept} onChange={onChange} /></div>;
    case 'waveform':
      return <div className="py-1"><WaveformControl compact input={field} value={(value as FileRef | null) ?? null} trace={traces[traceKey(sweepId, cell.key, field.id)]} onChange={onChange} /></div>;
  }
}

/**
 * A sweep on the fill page: a grid of rows by columns (with a tab for each
 * value of anything on the Tabs shelf), or a flat table of one row per cell.
 * Tab moves right; Enter moves down, and on the last row to the next tab.
 */
export function SweepControl({ id, sheet, sweep, inputs, onChange }: { id?: string; sheet: InputSheet; sweep: SweepInput; inputs: InputValues; onChange: (value: unknown, cell?: SweepCellChange) => void }) {
  const { traces } = useFill();
  const grid = useMemo(() => sweepGrid(sheet, sweep, inputs), [sheet, sweep, inputs]);
  const [view, setView] = useState<'grid' | 'flat'>(() => sweepView(sweep));
  const byId = new Map(grid.conditions.map((c) => [c.id, c]));
  const shelf = (ids: string[]) => ids.map((i) => byId.get(i)).filter((c): c is ResolvedCondition => !!c);
  const [tabConds, rowConds, colConds] = [shelf(sweep.layout.tabs), shelf(sweep.layout.rows), shelf(sweep.layout.columns)];
  const tabs = combos(tabConds);
  const rows = combos(rowConds);
  const cols = combos(colConds);
  const [tab, setTab] = useState(0);
  const activeTab = tabs[Math.min(tab, Math.max(0, tabs.length - 1))] ?? {};
  const anyFilled = grid.cells.some((c) => Object.keys(c.values).length > 0);
  const needs = (field: CellInput, cell: GridCell) => anyFilled && !!sweep.required && field.required !== false && !isInputFilled(field, cell.values[field.id]);
  const set = (cell: GridCell, field: CellInput) => (v: unknown) => onChange(setSweepCell(sweep, inputs[sweep.id], cell.at, field.id, v), { at: cell.at, field: field.id, value: v });
  const filledIn = (cells: GridCell[]) => cells.reduce((n, c) => n + sweep.record.filter((f) => isInputFilled(f, c.values[f.id])).length, 0);

  // Enter moves down; on the last row of a tab, to the same column on the next tab
  const table = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<{ row: number; col: number } | null>(null);
  useEffect(() => {
    const want = pendingFocus.current;
    if (!want) return;
    pendingFocus.current = null;
    table.current?.querySelector<HTMLElement>(`[data-row="${want.row}"][data-col="${want.col}"]`)?.focus();
  }, [tab]);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (e.key !== 'Enter' || target.tagName === 'TEXTAREA' || target.dataset.row === undefined) return;
    e.preventDefault();
    const row = Number(target.dataset.row) + (e.shiftKey ? -1 : 1);
    const col = Number(target.dataset.col);
    const next = table.current?.querySelector<HTMLElement>(`[data-row="${row}"][data-col="${col}"]`);
    if (next) next.focus();
    else if (!e.shiftKey && view === 'grid' && tab < tabs.length - 1) {
      pendingFocus.current = { row: 0, col };
      setTab(tab + 1);
    }
  };

  const empty = grid.cells.length === 0;
  const onlyUnit = sweep.record.length === 1 && sweep.record[0].kind === 'number' ? sweep.record[0].unit : undefined;
  const cornerLabel = `${rowConds.map((c) => c.label).join(' · ') || ''}${rowConds.length ? ' ↓' : ''}${colConds.length ? ` · ${colConds.map((c) => c.label).join(' · ')} →` : ''}`;

  return (
    <div id={id} className="flex flex-col gap-2" onKeyDown={onKeyDown} ref={table}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {empty
            ? I18n.get('rv2SweepNoCells')
            : I18n.get('rv2SweepCells', { cells: String(grid.cells.length), conditions: grid.conditions.map((c) => `${c.values.length} ${c.label.toLowerCase()}`).join(' × ') })}
        </span>
        <Segmented size="sm" value={view} onChange={(v) => v && setView(v)} options={[{ value: 'grid', label: I18n.get('rv2Grid') }, { value: 'flat', label: I18n.get('rv2FlatTable') }]} />
      </div>

      {empty ? null : view === 'grid' ? (
        <div className="overflow-hidden rounded-lg border-[1px]">
          {tabs.length > 1 && (
            <div role="tablist" className="flex gap-1 overflow-x-auto border-b-[1px] px-1.5 pt-1.5">
              {tabs.map((at, i) => {
                const cells = grid.cells.filter((c) => tabConds.every((tc) => c.at[tc.id] === at[tc.id]));
                const done = filledIn(cells);
                const total = cells.length * sweep.record.length;
                return (
                  <button key={i} type="button" role="tab" aria-selected={i === tab} onClick={() => setTab(i)}
                    className={cn('flex flex-col items-start rounded-t-md border-b-2 px-[0.75rem] py-1.5 text-left', i === tab ? 'border-sky-700 bg-muted/60' : 'border-transparent hover:bg-muted/40')}>
                    <span className="flex items-center gap-1 text-sm font-medium">{done === total && <Check className="size-3.5 text-emerald-600" />}{describe(tabConds, at)}</span>
                    <span className="text-xs text-muted-foreground">{I18n.get('rv2OfTotal', { done: String(done), total: String(total) })}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th scope="col" className="h-9 w-48 border-r-[1px] border-b-[1px] px-2.5 text-left font-normal text-muted-foreground">{cornerLabel}</th>
                  {cols.map((at, c) => (
                    <th key={c} scope="col" colSpan={sweep.record.length} className="h-9 border-b-[1px] px-2.5 text-left font-medium">{describe(colConds, at) || sweep.label}</th>
                  ))}
                </tr>
                {sweep.record.length > 1 && (
                  <tr className="bg-muted/30">
                    <th className="border-r-[1px] border-b-[1px]" />
                    {cols.flatMap((_, c) => sweep.record.map((f) => <th key={`${c}-${f.id}`} scope="col" className="h-7 border-b-[1px] px-2.5 text-left text-xs font-normal text-muted-foreground">{f.label}{f.kind === 'number' && f.unit ? ` (${f.unit})` : ''}{f.kind === 'waveform' && captureColumns(f) ? ` · ${captureColumns(f)}` : ''}</th>))}
                  </tr>
                )}
              </thead>
              <tbody>
                {rows.map((rowAt, r) => (
                  <tr key={r} className="border-b-[1px] last:border-b-0">
                    <th scope="row" className="h-9 border-r-[1px] px-2.5 text-left font-medium">{describe(rowConds, rowAt) || sweep.label}</th>
                    {cols.flatMap((colAt, c) => {
                      const cell = grid.cell({ ...activeTab, ...rowAt, ...colAt });
                      return sweep.record.map((field, f) => (
                        <td key={`${c}-${field.id}`} className="min-w-28 px-1.5 py-0">
                          <CellEditor field={field} cell={cell} sweepId={sweep.id} value={cell.values[field.id]} traces={traces} onChange={set(cell, field)}
                            nav={{ row: r, col: c * sweep.record.length + f }} needed={needs(field, cell)}
                            label={`${field.label}, ${describe(grid.conditions, cell.at)}`} />
                        </td>
                      ));
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <FlatTable sweep={sweep} grid={grid.cells} conditions={shelfOrder(sweep).map((i) => byId.get(i)).filter((c): c is ResolvedCondition => !!c)} needs={needs} set={set} traces={traces} />
      )}

      {!empty && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{onlyUnit ? I18n.get('rv2UnitFixed', { unit: onlyUnit }) : ''}</span>
          <span>{view === 'grid' ? I18n.get('rv2GridKeys') : I18n.get('rv2FlatSummary', { rows: String(grid.cells.length), values: String(grid.cells.length * sweep.record.length), filled: String(filledIn(grid.cells)) })}</span>
        </div>
      )}
    </div>
  );
}

/** One row per cell: a column per condition, then a column per recorded input. Filterable by condition and by what is left. */
function FlatTable({ sweep, grid, conditions, needs, set, traces }: {
  sweep: SweepInput; grid: GridCell[]; conditions: ResolvedCondition[]; needs: (f: CellInput, c: GridCell) => boolean; set: (c: GridCell, f: CellInput) => (v: unknown) => void; traces: Record<string, RunTrace>;
}) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [onlyEmpty, setOnlyEmpty] = useState(false);
  // the grid lists cells tabs-first, then rows, then columns: the shelf order
  const shown = grid.filter((cell) => conditions.every((c) => !filters[c.id] || String(cell.at[c.id]) === filters[c.id])
    && (!onlyEmpty || sweep.record.some((f) => !isInputFilled(f, cell.values[f.id]))));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {conditions.map((c) => (
          <select key={c.id} aria-label={c.label} value={filters[c.id] ?? ''} onChange={(e) => setFilters({ ...filters, [c.id]: e.target.value })}
            className="h-7 rounded-md border-[1px] border-input bg-background px-2 text-xs">
            <option value="">{I18n.get('rv2FilterAll', { condition: c.label })}</option>
            {c.values.map((v) => <option key={String(v)} value={String(v)}>{formatConditionValue(c, v)}</option>)}
          </select>
        ))}
        <select aria-label={I18n.get('rv2Show')} value={onlyEmpty ? 'empty' : 'all'} onChange={(e) => setOnlyEmpty(e.target.value === 'empty')} className="h-7 rounded-md border-[1px] border-input bg-background px-2 text-xs">
          <option value="all">{I18n.get('rv2ShowAll')}</option>
          <option value="empty">{I18n.get('rv2ShowEmpty')}</option>
        </select>
      </div>
      <div className="overflow-x-auto rounded-lg border-[1px]">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/30 text-[0.7rem] tracking-wide text-muted-foreground uppercase">
              <th colSpan={conditions.length + 1} className="h-7 border-r-[1px] px-2.5 text-left font-medium">{I18n.get('rv2Conditions')}</th>
              <th colSpan={sweep.record.length} className="h-7 px-2.5 text-left font-medium">{I18n.get('rv2RecordedInEachCell', { count: String(sweep.record.length) })}</th>
            </tr>
            <tr className="bg-muted/50 text-left">
              <th className="h-9 w-10 border-b-[1px] px-2.5 font-medium text-muted-foreground">#</th>
              {conditions.map((c, i) => (
                <th key={c.id} scope="col" className={cn('border-b-[1px] px-2.5 font-medium', i === conditions.length - 1 && 'border-r-[1px]')}>
                  {c.label}{c.unit ? <span className="block text-xs font-normal text-muted-foreground">{c.unit}</span> : null}
                </th>
              ))}
              {sweep.record.map((f) => (
                <th key={f.id} scope="col" className="min-w-32 border-b-[1px] px-2.5 font-medium">
                  {f.label}<span className="block text-xs font-normal text-muted-foreground">{I18n.get(`rv2Kind_${f.kind}`)}{f.kind === 'number' && f.unit ? ` · ${f.unit}` : ''}{f.kind === 'waveform' && captureColumns(f) ? ` · ${captureColumns(f)}` : ''}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((cell) => {
              const n = grid.indexOf(cell);
              return (
                <tr key={cell.key} className="border-b-[1px] last:border-b-0">
                  <td className="px-2.5 text-muted-foreground tabular-nums">{n + 1}</td>
                  {conditions.map((c, i) => <td key={c.id} className={cn('h-9 px-2.5 tabular-nums', i === conditions.length - 1 && 'border-r-[1px]')}>{String(cell.at[c.id])}</td>)}
                  {sweep.record.map((f, j) => (
                    <td key={f.id} className="px-1.5 py-0">
                      <CellEditor field={f} cell={cell} sweepId={sweep.id} value={cell.values[f.id]} traces={traces} onChange={set(cell, f)} nav={{ row: n, col: j }} needed={needs(f, cell)}
                        label={`${f.label}, ${conditions.map((c) => formatConditionValue(c, cell.at[c.id])).join(', ')}`} />
                    </td>
                  ))}
                </tr>
              );
            })}
            {shown.length === 0 && <tr><td colSpan={conditions.length + sweep.record.length + 1} className="h-[2.5rem] px-2.5 text-muted-foreground">{I18n.get('rv2NoRowsMatch')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
